/* eslint func-style: "off" */
import get from 'lodash/get';
import isPlainObject from 'lodash/isPlainObject';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import omit from 'lodash/omit';
import difference from 'lodash/difference';
import keyBy from 'lodash/keyBy';

import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';

import apiClient from '../../helpers/apiClient';
import { convertDatePickerTimeToApiTime } from '../../helpers/timeFormat';
import { generateUrlFromPathWithParams } from '../../helpers/urlGenerator';
import {
  //actions
  SUBMIT_QUERY,
  SAVE_TO_GROUP,
  SOCKET_UPDATE,
  SEARCH_DISCOVER,
  TOGGLE_VIEW_BY_VALUE,
  SET_GRAPH_HOVER,
  // action creators
  fetchSearchResults,
  fetchSearchResultsFailed,
  fetchSearchResultsSucceeded,
  saveToGroupFailed,
  saveToGroupSucceeded,
  updateAuthors,
  setGraphHoverData,
  joinBotIntelligenceRoom,
  leaveBotIntelligenceRoom
} from './BotIntelligence.ducks';
import {
  createClusterGraphData,
  filterNodes,
  formatTwitterUsername
} from './dataUtils';

import {
  //actions
  FETCH_GROUPS,
  // action creators
  clearModalForm,
  fetchGroupsFailed,
  fetchGroupsSucceeded
} from './containers/AddToGroupModal/AddToGroupModal.ducks';

import {
  FETCH_SMART_FILTER,
  fetchSmartFilterSucceeded,
  fetchSmartFilterFailed
} from './containers/SearchFilters/SearchFilters.ducks';


import { VIEW_BY_OPTIONS } from './constants';

const getAccountId = (state) => state.AppUser.currentAccountId;
const getEndTime = (state) => state.BotIntelligence.SearchFilters.endDate;
const getProfileIds = (state) => state.BotIntelligence.SearchFilters.profileIds;
const getSmartFilterId = (state) => state.BotIntelligence.SearchFilters.selectedSmartFilterId;
const getSmartFilterSummary = (state) => state.BotIntelligence.SearchFilters.selectedSmartFilterSummary;
const getQuery = (state) => state.BotIntelligence.SearchFilters.query;
const getSearchBy = (state) => state.BotIntelligence.SearchFilters.searchBy;
const getStartTime = (state) => state.BotIntelligence.SearchFilters.startDate;
const getAuthorsById = (state) => state.BotIntelligence.Page.authorsById;
const getSelectedViewByOption = (state) => state.BotIntelligence.Page.selectedViewByOption;
const getSelectedTopClustersByVolume = (state) => state.BotIntelligence.Page.selectedTopClustersByVolume;
const getSelectedTooltipAuthor = (state) => state.BotIntelligence.Page.selectedTooltipAuthor;
const getGroupDestinationFields = (state) => state.BotIntelligence.Page.groupDestinationFields;
const getSearchTooltipAuthor = (state) => state.BotIntelligence.Page.searchTooltipAuthor;
const getViewBy = (state) => state.BotIntelligence.Page.viewBy;
const getSelectedCampaign = (state) => state.BotIntelligence.AddToGroupModal.selectedCampaign;
const getSelectedGroup = (state) => state.BotIntelligence.AddToGroupModal.selectedGroup;
const getNewGroupName = (state) => state.BotIntelligence.AddToGroupModal.newGroupName;
const getAddTo = (state) => state.BotIntelligence.AddToGroupModal.addTo;
const getFilteredAutomationScore = (state) => state.BotIntelligence.Page.filteredAutomationScore;
const getAuthorNodes = (state) => state.BotIntelligence.Page.authorNodes;
const getClusterNodesByType = (state) => state.BotIntelligence.Page.clusterNodesByType;
const getClusterHoverMapsByType = (state) => state.BotIntelligence.Page.clusterHoverMapsByType;
const getTimezone = (state) => state.AppUser.user.timezone;

const _addHoverLinkAndClusterIds = (hoverData, node, clusterBy) => {
  const { id, targetClusters={} } = node;
  const { hoverId, clusterIds, linkIds } = hoverData;
  const isNotHoverNode = hoverId !== id;
  Object.keys(targetClusters)
    .filter((clusterId) => clusterId.indexOf(clusterBy) === 0)
    .forEach((clusterId) => {
      clusterIds[clusterId] = true;
      if (isNotHoverNode){
        // links attached to hover node will highlight be default,
        // so we only need to specify the linkIds when the node is not the hover node
        linkIds[`${id}|${clusterId}`] = true;
      }
    });
};

const _createGenericError = (override) => {
  const errorMessage = {
    type: 'error',
    title: 'There was a problem',
    contentMsg: 'An error has occurred. Please try again later.'
  };
  return Object.assign(errorMessage, override);
};

const _getCreateGroupProperties = (selectedCampaign, newGroupName) => {
  return {
    path: `/api/campaigns/${selectedCampaign.value}/author_groups`,
    method: 'POST',
    bodyParams: {
      name: newGroupName,
      targets: []
    }
  };
};

const _getEditGroupProperties = (selectedCampaign, selectedGroup) => {
  return {
    path: `/api/campaigns/${selectedCampaign.value}/author_groups/${selectedGroup._id}`,
    method: 'PUT',
    bodyParams: omit(selectedGroup, ['label', 'value', 'id'])
  };
};

const submitQuery = function* (action) {
  try {
    const { campaignId } = action.payload;
    const accountId = yield select(getAccountId);
    const profileIds = yield select(getProfileIds);
    const smartFilterId = yield select(getSmartFilterId);
    const smartFilterSummary = yield select(getSmartFilterSummary);
    const query = yield select(getQuery);
    const searchBy = yield select(getSearchBy);
    const startTime = yield select(getStartTime);
    const endTime = yield select(getEndTime);
    const timezone = yield select(getTimezone);

    const postBody = {
      startTime: convertDatePickerTimeToApiTime(startTime, timezone),
      endTime: convertDatePickerTimeToApiTime(endTime, timezone)
    };
    if(searchBy === 'profiles') {
      if (profileIds.length === 0){ return; }
      postBody.profileIds = profileIds;
      postBody.accountId = accountId;
      postBody.campaignId = campaignId;

      if (smartFilterId) {
        postBody.filterId = smartFilterId;
      }
    } else {
      if (!query){ return; }
      postBody.query = query;
    }
    yield put(fetchSearchResults());

    const { searchResults, missingAuthorIds } = yield call(apiClient, `/api/bot-detection/authors-by-${searchBy}`, {
      method: 'POST',
      body: JSON.stringify(postBody)
    });

    // index by authorId for quick look up
    const authorsById = keyBy(searchResults, 'author.id');

    if (missingAuthorIds.length){
      const foundAuthorIds = difference(Object.keys(authorsById), missingAuthorIds);
      yield put(joinBotIntelligenceRoom(foundAuthorIds, missingAuthorIds));
    }

    const viewBy = yield select(getViewBy);
    const clusterGraphData = createClusterGraphData(authorsById, viewBy);

    yield put(fetchSearchResultsSucceeded({
      ...clusterGraphData,
      missingAuthorIds,
      authorsById,
      lastSearchBy: searchBy,
      smartFilterSummaryOnSearch: (searchBy === 'profiles') ? smartFilterSummary : null
    }));
  } catch (e) {
    const errorMsg = _createGenericError({title: 'Could not fetch search results'});
    yield put(fetchSearchResultsFailed(errorMsg));
  }
};

const refreshNodes = function* () {
  const authorsById = yield select(getAuthorsById);

  const viewBy = yield select(getViewBy);
  const clusterGraphData = createClusterGraphData(authorsById, viewBy);

  yield put(updateAuthors({
    ...clusterGraphData
  }));
};

const updateAuthorsResult = function* (action) {
  const oldAuthorsById = yield select(getAuthorsById);
  const { authors, remainingAuthors } = action.payload;
  const authorsById = Object.assign({}, oldAuthorsById);

  authors.forEach((author) => {
    const authorData = authorsById[author.id];
    if (authorData){
      authorsById[author.id] = Object.assign(authorData, { author });
    }
  });


  yield put(updateAuthors({authorsById, remainingAuthors}));
  if (remainingAuthors === 0){
    yield put(leaveBotIntelligenceRoom());
  }
};

const fetchGroups = function* () {
  try {
    const selectedCampaign = yield select(getSelectedCampaign);
    const path = `/api/campaigns/${selectedCampaign.value}/author_groups/`;
    const payload = yield call(apiClient, path);
    const groups = map(payload, (group) => {
      return {value: group._id, label: group.name, id: group._id, ...group};
    });
    yield put(fetchGroupsSucceeded(groups));
  } catch (e) {
    // this does nothing at the moment
    yield put(fetchGroupsFailed(e.message));
  }
};

const replaceArray = (arrayOfString) =>  {
  if (isEmpty(arrayOfString)) {
    return [];
  }
  const result = [];
  const regex = new RegExp('@', 'g');
  arrayOfString.forEach((item) => {
    result.push(item.replace(regex, ''));
  });
  return result;
};

const saveToGroup = function* () {
  try {
    const addTo = yield select(getAddTo);
    const selectedCampaign = yield select(getSelectedCampaign);
    const newGroupName = yield select(getNewGroupName);
    const selectedGroup = yield select(getSelectedGroup);
    const filteredAutomationScore = yield select(getFilteredAutomationScore);
    const authorNodes = yield select(getAuthorNodes);
    const clusterNodesByType = yield select(getClusterNodesByType);
    const selectedClusters = yield select(getSelectedTopClustersByVolume);
    const { value: viewByValue, label: viewByLabel } = yield select(getSelectedViewByOption);
    const selectedTooltipAuthor = yield select(getSelectedTooltipAuthor);

    const isNewGroup = addTo === 'newGroup';
    const apiProperties = isNewGroup
      ? _getCreateGroupProperties(selectedCampaign, newGroupName)
      : _getEditGroupProperties(selectedCampaign, selectedGroup);

    let groupDestinationFields = yield select(getGroupDestinationFields);

    if (selectedTooltipAuthor) {
      groupDestinationFields = [
        ...groupDestinationFields,
        selectedTooltipAuthor
      ];
    } else if (!isEmpty(clusterNodesByType)) {
      const clusterNodes = clusterNodesByType[viewByValue];
      const nodes = filterNodes({
        authorNodes,
        clusterNodes,
        filteredAutomationScore,
        selectedClusters
      });

      const selectedTooltipAuthors = nodes.reduce((results, {nodeType, data={}}) => {
        const { author={} } = data;
        const { username } = author;
        if (nodeType !== 'cluster' && username) {
          // TODO change if Influence Intetelligence uses other sources
          results.push({
            type: 'author',
            targetName: 'twitter_author',
            targetLabel: 'Twitter Author',
            targetValue: formatTwitterUsername(username)
          });
        }
        return results;
      }, []);

      groupDestinationFields = [
        ...groupDestinationFields,
        ...selectedTooltipAuthors
      ];
    }

    let newTargets = 0;
    if (groupDestinationFields.length > 0) {
      if (isEmpty(apiProperties.bodyParams.targets)) {
        apiProperties.bodyParams.targets = {};
      }
      groupDestinationFields.forEach(gdf => {
        const { targetName, targetValue } = gdf;
        if (isEmpty(apiProperties.bodyParams.targets[targetName])) {
          apiProperties.bodyParams.targets[targetName] = [targetValue];
          newTargets += 1;
        } else if (!apiProperties.bodyParams.targets[targetName].includes(targetValue)) {
          // do not add duplicates
          apiProperties.bodyParams.targets[targetName].push(targetValue);
          newTargets += 1;
        }
      });
    }

    if (newTargets) {
      yield call(apiClient, apiProperties.path, {
        method: apiProperties.method,
        body: JSON.stringify(apiProperties.bodyParams)
      });
    }

    const bannerProps = {
      type: 'success',
      title: `Added to ${isNewGroup ? 'New ' : ''}Group`,
      contentMsg: `The selected attributes of the ${newTargets > 1 ? 'stories' : 'story'} ` +
        `viewed by ${viewByLabel.toLowerCase()} were added to ${apiProperties.bodyParams.name}`,
      params: apiProperties
    };
    yield put(saveToGroupSucceeded(bannerProps));
    yield put(clearModalForm());
  } catch (e) {
    const errorMsg = _createGenericError({title: 'Could not save to group'});
    const validationError = get(e, 'body.validationErrors.authors[0]');
    const standardError = get(e, 'body.error');
    errorMsg.contentMsg = validationError || standardError || errorMsg.contentMsg;
    yield put(saveToGroupFailed(errorMsg));
  }
};

const searchDiscover = function* () {
  const endTime = yield select(getEndTime);
  const startTime = yield select(getStartTime);
  const timezone = yield select(getTimezone);

  const selectedTopClustersByVolume = yield select(getSelectedTopClustersByVolume);
  const searchTooltipAuthor = yield select(getSearchTooltipAuthor);
  const { value: valueOption } = yield select(getSelectedViewByOption);

  let searchPattern = (value) => `@${value}`;
  if (!searchTooltipAuthor) {
    searchPattern = find(VIEW_BY_OPTIONS.all, {value: valueOption}).searchPattern;
  }
  const list = searchTooltipAuthor
    ? [searchTooltipAuthor]
    // remove prefix of cluster type
    : selectedTopClustersByVolume.map((cluster) => cluster.split('|').slice(1).join(''));

  const q = JSON.stringify(list.map(searchPattern));
  const start = convertDatePickerTimeToApiTime(startTime, timezone);
  const end = convertDatePickerTimeToApiTime(endTime, timezone);
  const link = generateUrlFromPathWithParams('/discover/', { start, end, q });
  window.open(link, '_blank');
};

const setGraphHover = function* (action) {
  const { payload: { node, isClusterListHover } } = action;
  let hoverData = {};
  // clear graph hover data if node is null
  if (!node){
    return yield put(setGraphHoverData(hoverData));
  }
  const viewBy = yield select(getViewBy);
  const { value: clusterBy } = yield select(getSelectedViewByOption);
  const { nodeType, id: hoverId, categoryId } = node;
  if (nodeType === 'author'){
    hoverData = {
      hoverId,
      nodeType,
      categoryId, // only for category graphs, also used by tooltip to show correct story
      clusterBy, // used by tooltip to show correct story
      tooltipNode: node,
      authorIds: { [hoverId]: true },
      clusterIds: {},
      linkIds: {}
    };
    if (viewBy === 'cluster'){ // only in cluster graph should we check for linked clusters
      // add links and clusters linked to author node
      _addHoverLinkAndClusterIds(hoverData, node, clusterBy);
    }
  }
  else if (nodeType === 'cluster'){
    const clusterHoverMapsByType = yield select(getClusterHoverMapsByType);
    const clusterHoverMapById = clusterHoverMapsByType[clusterBy] || {};
    hoverData = Object.assign({isClusterListHover}, clusterHoverMapById[hoverId] || {});
  }
  return yield put(setGraphHoverData(hoverData));
};

const fetchSmartFilter = function* (action) {
  const { filterId } = action.payload;
  try {
    const data  = yield call(apiClient, '/api/analytics/filters', {
      method: 'POST',
      body: JSON.stringify(action.payload)
    });

    yield put(fetchSmartFilterSucceeded({
      filterId,
      smartFilter: data,
    }));
  } catch (e) {
    const errorMsg = _createGenericError({title: 'Could not fetch smart filter summary'});
    yield put(fetchSmartFilterFailed(errorMsg));
  }
};

const searchDiscoverSaga = function* () {
  yield takeLatest( SEARCH_DISCOVER, searchDiscover);
};

const updateAuthorsResultSaga = function* () {
  yield takeLatest( SOCKET_UPDATE, updateAuthorsResult);
};

const refreshNodesSaga = function* () {
  yield takeLatest( TOGGLE_VIEW_BY_VALUE, refreshNodes);
};

const saveToGroupSaga = function* () {
  yield takeLatest( SAVE_TO_GROUP, saveToGroup);
};

const fetchSearchResultsSaga = function* () {
  yield takeLatest( SUBMIT_QUERY, submitQuery);
};

const fetchGroupsSaga = function* () {
  yield takeLatest( FETCH_GROUPS, fetchGroups);
};

const setGraphHoverSaga = function* () {
  yield takeLatest( SET_GRAPH_HOVER, setGraphHover);
};

const fetchSmartFilterSaga = function* () {
  yield takeLatest( FETCH_SMART_FILTER, fetchSmartFilter);
};

const sagas = function* () {
  yield all([
    fork(searchDiscoverSaga),
    fork(fetchGroupsSaga),
    fork(fetchSearchResultsSaga),
    fork(saveToGroupSaga),
    fork(updateAuthorsResultSaga),
    fork(refreshNodesSaga),
    fork(setGraphHoverSaga),
    fork(fetchSmartFilterSaga)
  ]);
};

export default sagas;
export {
  // protected
  _getEditGroupProperties,
  _getCreateGroupProperties,
  // public,
  getAccountId,
  getEndTime,
  getProfileIds,
  getSmartFilterId,
  getSmartFilterSummary,
  getQuery,
  getSearchBy,
  getStartTime,
  getSelectedCampaign,
  getSelectedGroup,
  getSelectedViewByOption,
  getSelectedTopClustersByVolume,
  getNewGroupName,
  getAddTo,
  getAuthorsById,
  getSelectedTooltipAuthor,
  getSearchTooltipAuthor,
  getViewBy,
  getFilteredAutomationScore,
  getAuthorNodes,
  getClusterNodesByType,
  getClusterHoverMapsByType,
  getTimezone,
  getGroupDestinationFields,
  submitQuery,
  saveToGroup,
  searchDiscover,
  setGraphHover,
  fetchGroups,
  fetchSmartFilter,
  refreshNodes,
  updateAuthorsResult,
  searchDiscoverSaga,
  fetchGroupsSaga,
  fetchSearchResultsSaga,
  saveToGroupSaga,
  updateAuthorsResultSaga,
  refreshNodesSaga,
  setGraphHoverSaga,
  fetchSmartFilterSaga
};
